import type {
  RimType,
  TirePriceBand,
  TirePriceTables,
  VehicleClass,
} from '@/content/types';
import {
  HOURLY_MAX,
  HOURLY_MIN,
  HOURLY_STEP,
} from '@/content/et/pricing';

export type { VehicleClass, RimType };

export interface TireChangeQuery {
  vehicleClass: VehicleClass;
  rimType: RimType;
  /** Required unless shouldHideTireSize(...) is true */
  sizeInches?: number;
}

export function shouldHideTireSize(
  vehicleClass: VehicleClass,
  rimType: RimType,
): boolean {
  return vehicleClass === 'vanSuv' && rimType === 'steel';
}

function bandsFor(
  vehicleClass: VehicleClass,
  rimType: RimType,
  tables: TirePriceTables,
): TirePriceBand[] {
  return tables[vehicleClass][rimType];
}

/** Default suurus range for passenger alloy (13'–23'). */
export const TIRE_SIZE_UI_MIN = 13;
export const TIRE_SIZE_UI_MAX = 23;
/** Plekkvelg (steel) max size — matrix only prices up to 18'. */
export const TIRE_SIZE_STEEL_MAX = 18;
/** Kaubik/maastur valuvelg — matrix starts at 15'. */
export const TIRE_SIZE_VAN_ALLOY_MIN = 15;

/**
 * Inch options for the suurus stepper.
 * - Van + steel: empty (control hidden)
 * - Steel (plekkvelg): 13–18 only
 * - Van + alloy: 15–23
 * - Passenger alloy: 13–23
 */
export function listTireSizeOptions(
  vehicleClass: VehicleClass,
  rimType: RimType,
  _tables: TirePriceTables,
): number[] {
  if (shouldHideTireSize(vehicleClass, rimType)) return [];

  let min = TIRE_SIZE_UI_MIN;
  let max = TIRE_SIZE_UI_MAX;

  if (rimType === 'steel') {
    max = TIRE_SIZE_STEEL_MAX;
  } else if (vehicleClass === 'vanSuv' && rimType === 'alloy') {
    min = TIRE_SIZE_VAN_ALLOY_MIN;
    max = TIRE_SIZE_UI_MAX;
  }

  const sizes: number[] = [];
  for (let i = min; i <= max; i++) {
    sizes.push(i);
  }
  return sizes;
}

/** Sizes that have a price band for this combo (matrix-accurate). */
export function listPricedTireSizeOptions(
  vehicleClass: VehicleClass,
  rimType: RimType,
  tables: TirePriceTables,
): number[] {
  if (shouldHideTireSize(vehicleClass, rimType)) return [];

  const bands = bandsFor(vehicleClass, rimType, tables);
  const sizes = new Set<number>();
  for (const band of bands) {
    for (let i = band.minInch; i <= band.maxInch; i++) {
      sizes.add(i);
    }
  }
  return Array.from(sizes).sort((a, b) => a - b);
}

/**
 * Quote tire change for a full vehicle (4 wheels).
 * vanSuv + steel → always 45€ regardless of size.
 */
export function quoteTireChange(
  query: TireChangeQuery,
  tables: TirePriceTables,
): number | null {
  const { vehicleClass, rimType, sizeInches } = query;
  const bands = bandsFor(vehicleClass, rimType, tables);

  // Van steel is size-agnostic
  if (shouldHideTireSize(vehicleClass, rimType)) {
    return bands[0]?.priceEur ?? null;
  }

  if (sizeInches == null || Number.isNaN(sizeInches)) {
    return null;
  }

  const band = bands.find(
    (b) => sizeInches >= b.minInch && sizeInches <= b.maxInch,
  );
  return band ? band.priceEur : null;
}

export function quoteHourly(hours: number, rateEur: number): number {
  return Math.round(hours * rateEur * 100) / 100;
}

export function isValidHourlyHours(hours: number): boolean {
  if (Number.isNaN(hours)) return false;
  if (hours < HOURLY_MIN || hours > HOURLY_MAX) return false;
  const steps = Math.round((hours - HOURLY_MIN) / HOURLY_STEP);
  const snapped = HOURLY_MIN + steps * HOURLY_STEP;
  return Math.abs(snapped - hours) < 1e-9;
}

export function snapHourlyHours(hours: number): number {
  const clamped = Math.min(HOURLY_MAX, Math.max(HOURLY_MIN, hours));
  const steps = Math.round((clamped - HOURLY_MIN) / HOURLY_STEP);
  return Math.round((HOURLY_MIN + steps * HOURLY_STEP) * 10) / 10;
}
